import {
  createContext, useCallback, useContext, useMemo, useRef, useState,
} from "react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/**
 * Imperative confirm() wrapped around shadcn AlertDialog.
 *
 * Why we have this:
 *   `window.confirm()` is silently blocked inside the Codex preview
 *   iframe — clicks fire, browser kills the dialog, the calling JS exits
 *   before any API call. This bug bit us in three separate places (SMS
 *   chat, warranty wizard, warranty admin) before we centralised. Any new
 *   "are you sure?" prompt should now use `useConfirm()`.
 *
 * Usage:
 *   const confirm = useConfirm();
 *   const ok = await confirm({
 *     title: "Discard this draft?",
 *     description: "All uploaded media will be deleted permanently.",
 *     confirmLabel: "Discard draft",
 *     tone: "danger",
 *     testId: "warranty-discard-confirm",
 *   });
 *   if (!ok) return;
 *   // … perform the action …
 *
 * Mount `<ConfirmProvider>` once near the app root (see App.js). The
 * provider holds a single AlertDialog instance and resolves the promise
 * returned by confirm() with `true`/`false`.
 */

const ConfirmContext = createContext(null);

const DEFAULT_OPTS = {
  title: "Are you sure?",
  description: "",
  confirmLabel: "Confirm",
  cancelLabel: "Cancel",
  tone: "default", // "default" | "danger"
  testId: "confirm-dialog",
};

export function ConfirmProvider({ children }) {
  const [opts, setOpts] = useState(null); // null = closed
  const resolverRef = useRef(null);

  const confirm = useCallback((options = {}) => new Promise((resolve) => {
    // Only one prompt at a time — if a previous resolver is still pending,
    // resolve it as cancelled so we don't leak promises.
    if (resolverRef.current) {
      try { resolverRef.current(false); } catch { /* noop */ }
    }
    resolverRef.current = resolve;
    setOpts({ ...DEFAULT_OPTS, ...options });
  }), []);

  const settle = useCallback((value) => {
    setOpts(null);
    if (resolverRef.current) {
      const r = resolverRef.current;
      resolverRef.current = null;
      r(value);
    }
  }, []);

  // Memoise the value so children don't re-render on every state change.
  const value = useMemo(() => confirm, [confirm]);

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      <AlertDialog open={!!opts} onOpenChange={(open) => !open && settle(false)}>
        <AlertDialogContent data-testid={opts?.testId || DEFAULT_OPTS.testId}>
          <AlertDialogHeader>
            <AlertDialogTitle>{opts?.title}</AlertDialogTitle>
            {opts?.description && (
              <AlertDialogDescription>{opts.description}</AlertDialogDescription>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => settle(false)}
              data-testid={`${opts?.testId || DEFAULT_OPTS.testId}-cancel`}
            >
              {opts?.cancelLabel}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => settle(true)}
              data-testid={`${opts?.testId || DEFAULT_OPTS.testId}-ok`}
              className={
                opts?.tone === "danger"
                  ? "bg-rose-600 hover:bg-rose-700 text-white"
                  : ""
              }
            >
              {opts?.confirmLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const fn = useContext(ConfirmContext);
  if (!fn) {
    throw new Error("useConfirm must be used inside <ConfirmProvider>");
  }
  return fn;
}
